import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBillingPlans } from '@/hooks/useBillingPlans';
import { UserRole } from '@/types/feedback';

const formSchema = z.object({
  fullName: z.string().min(1, {
    message: "Le nom est requis.",
  }),
  role: z.enum(['individual', 'corporate', 'admin']),
  organization: z.string().optional(),
  websiteUrl: z.string().url().optional().or(z.literal('')),
  plan: z.string(),
  corporateGroupId: z.string().optional().nullable()
});

type FormData = z.infer<typeof formSchema>;

type EditUserFormProps = {
  userId: string;
  email: string;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
};

const EditUserForm: React.FC<EditUserFormProps> = ({ 
  userId, 
  email, 
  isOpen, 
  onClose, 
  onUserUpdated 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { billingPlans, loading: plansLoading } = useBillingPlans();
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      role: 'individual',
      organization: '',
      websiteUrl: '',
      plan: 'free',
      corporateGroupId: null
    },
  });

  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen]);

  const fetchOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      
      setOrganizations(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des organisations:', error);
    } finally {
      setLoadingOrgs(false);
    }
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        console.log('Fetching details for user ID:', userId);
        
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (profileError) {
          console.error("Erreur lors de la récupération du profil:", profileError);
          throw profileError;
        }

        if (!profileData) {
          console.log('Profile not found, fetching from auth users...');
          const { data: authUsers, error: authError } = await supabase.functions.invoke('get-all-users');
          
          if (authError) throw authError;
          
          const authUser = Array.isArray(authUsers) ? authUsers.find(user => user.id === userId) : null;
          
          if (!authUser) {
            throw new Error("Utilisateur non trouvé");
          }
          
          console.log('Setting form data from auth user:', authUser);
          
          // Convertir les rôles anciens vers les nouveaux
          let userRole = authUser.user_metadata?.role || 'individual';
          
          // Normaliser le rôle selon le nouveau système
          userRole = normalizeRole(userRole);
          
          form.reset({
            fullName: authUser.user_metadata?.full_name || '',
            role: userRole as UserRole,
            organization: authUser.user_metadata?.organization || '',
            websiteUrl: authUser.user_metadata?.website_url || '',
            plan: 'free',
            corporateGroupId: null
          });
          
          return;
        }
        
        console.log('Setting form data from profile:', profileData);
        
        // Convertir les rôles anciens vers les nouveaux
        let userRole = profileData.role || 'individual';
        
        // Normaliser le rôle selon le nouveau système
        userRole = normalizeRole(userRole);
        
        form.reset({
          fullName: profileData.full_name || '',
          role: userRole as UserRole,
          organization: profileData.organization || '',
          websiteUrl: profileData.website_url || '',
          plan: profileData.plan || 'free',
          corporateGroupId: profileData.corporate_group_id
        });
      } catch (error: any) {
        console.error("Erreur lors de la récupération des détails utilisateur:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les détails de l'utilisateur.",
          variant: "destructive",
        });
        onClose();
      }
    };

    // Fonction utilitaire pour normaliser les rôles
    const normalizeRole = (role: string): UserRole => {
      if (role === 'candidat') return 'individual';
      if (role === 'entreprise' || role === 'recruteur') return 'corporate';
      if (role === 'admin') return 'admin';
      // Par défaut, utiliser 'individual' si le rôle n'est pas reconnu
      if (role !== 'individual' && role !== 'corporate' && role !== 'admin') {
        return 'individual';
      }
      return role as UserRole;
    };

    if (isOpen && userId) {
      fetchUserDetails();
    }
  }, [isOpen, userId, form]);

  const onSubmit = async (data: FormData) => {
    console.log("Soumission du formulaire avec les données:", data);
    setIsLoading(true);
    
    try {
      // Add detailed logging to help diagnose issues
      console.log(`Updating user ${userId} with plan: ${data.plan}`);
      
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', userId)
        .maybeSingle();

      let updateResult;
      
      if (existingProfile) {
        console.log("Updating existing profile with data:", data);
        updateResult = await supabase
          .from('profiles')
          .update({
            full_name: data.fullName,
            role: data.role,
            organization: data.organization || null,
            website_url: data.websiteUrl || null,
            plan: data.plan,
            corporate_group_id: data.corporateGroupId
          })
          .eq('id', userId);
      } else {
        console.log("Creating new profile with data:", data);
        updateResult = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: email,
            full_name: data.fullName,
            role: data.role,
            organization: data.organization || null,
            website_url: data.websiteUrl || null,
            plan: data.plan,
            is_active: true,
            corporate_group_id: data.corporateGroupId
          });
      }
      
      if (updateResult.error) {
        console.error("Erreur détaillée lors de la mise à jour:", updateResult.error);
        throw updateResult.error;
      }
      
      console.log("Profile updated successfully, updating user metadata");
      
      const { error: metadataError } = await supabase.functions.invoke('update-user-metadata', {
        body: {
          userId: userId,
          metadata: {
            full_name: data.fullName,
            role: data.role,
            organization: data.organization || null,
            website_url: data.websiteUrl || null,
            corporate_group_id: data.corporateGroupId
          },
          isAdminUpdate: true // Flag to indicate this is an admin-initiated update
        }
      });
      
      if (metadataError) {
        console.warn("Erreur lors de la mise à jour des métadonnées:", metadataError);
      }

      if (data.corporateGroupId) {
        const { data: existingMember, error: memberCheckError } = await supabase
          .from('organization_members')
          .select('id')
          .eq('organization_id', data.corporateGroupId)
          .eq('user_id', userId)
          .maybeSingle();
          
        if (memberCheckError) {
          console.warn("Erreur lors de la vérification d'appartenance:", memberCheckError);
        }
        
        if (!existingMember) {
          const { error: memberError } = await supabase
            .from('organization_members')
            .insert({
              organization_id: data.corporateGroupId,
              user_id: userId,
              role: data.role === 'admin' ? 'admin' : 'member' 
            });
            
          if (memberError) {
            console.warn("Erreur lors de l'ajout comme membre:", memberError);
          }
        }
      }

      toast({
        title: "Compte mis à jour",
        description: `Les informations de l'utilisateur ${email} ont été mises à jour.`,
      });
      
      onUserUpdated();
      onClose();
    } catch (error: any) {
      console.error("Erreur lors de la mise à jour du compte:", error);
      toast({
        title: "Erreur lors de la mise à jour",
        description: error.message || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show organization field conditionally based on role
  const showOrgField = form.watch('role') === 'corporate';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogTitle>Modifier l'utilisateur {email}</DialogTitle>
        <DialogDescription>
          Modifiez les informations de l'utilisateur ci-dessous.
        </DialogDescription>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom complet</FormLabel>
                  <FormControl>
                    <Input placeholder="Prénom Nom" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rôle</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un rôle" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="plan"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Forfait</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || 'free'}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un forfait" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {plansLoading ? (
                        <SelectItem value="loading">Chargement des forfaits...</SelectItem>
                      ) : (
                        billingPlans.map(plan => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name} {plan.price > 0 ? `(${plan.price}€)` : '(Gratuit)'}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="corporateGroupId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Organisation</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    value={field.value || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner une organisation" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">Aucune</SelectItem>
                      {loadingOrgs ? (
                        <SelectItem value="loading" disabled>Chargement des organisations...</SelectItem>
                      ) : (
                        organizations.map(org => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {showOrgField && (
              <>
                <FormField
                  control={form.control}
                  name="organization"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nom de l'entreprise</FormLabel>
                      <FormControl>
                        <Input placeholder="Nom de votre entreprise" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Site internet</FormLabel>
                      <FormControl>
                        <Input type="url" placeholder="https://www.exemple.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
          
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Mise à jour...' : 'Mettre à jour'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserForm;
